import {ChangeDetectionStrategy, Component, computed, CUSTOM_ELEMENTS_SCHEMA, inject, Signal} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {DsBoxModule, DsDimensionsConfig, DsModalModule} from '@bmw-ds/components';
import {IAuthInfo} from '@service-and-repairs/awpintegrationlib';
import {combineLatest, map, Observable} from 'rxjs';
import {AuthInfoFactory} from '../../auth/auth-info-factory';
import {AuthenticationTokenBearerService} from '../../auth/authentication-token-bearer.service';
import {VehicleSearchOrigin} from '../../core/analytics/enums/vehicle-search-origin';
import {ServiceCaseHolder} from '../../core/service-case/models/service-case-holder';
import {ServiceCaseManager} from '../../core/service-case/models/service-case-manager';
import {UserData} from '../../core/user/models/user-data';
import {UserService} from '../../core/user/services/user.service';
import {VehicleSearchService} from '../vehicle/vehicle-search-overlay/vehicle-search.service';
import {CustomerDataFrontendService} from './customer-data-frontend.service';
import {CustomerDataProfileService} from './customer-data-profile.service';

/**
 * Shows the Customer Data Frontend's customer profile web component in a modal overlay, so a customer's profile can be
 * opened from anywhere in the webapp without leaving the current app.
 */
@Component({
  selector: 'app-customer-data-profile',
  templateUrl: './customer-data-profile.component.html',
  styleUrls: ['./customer-data-profile.component.scss'],
  imports: [DsModalModule, DsBoxModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class CustomerDataProfileComponent {
  // The profile is docked to the right edge of the screen and uses the full viewport height (see the AWP design).
  protected readonly DIMENSIONS: DsDimensionsConfig = {width: '1680px', height: '100vh'};

  private readonly customerDataProfileService: CustomerDataProfileService = inject(CustomerDataProfileService);
  private readonly serviceCaseManager: ServiceCaseManager = inject(ServiceCaseManager);
  private readonly serviceCaseHolder: ServiceCaseHolder = inject(ServiceCaseHolder);
  private readonly userService: UserService = inject(UserService);
  private readonly vehicleSearchService: VehicleSearchService = inject(VehicleSearchService);

  private readonly authenticationTokenBearerService: AuthenticationTokenBearerService
    = inject(AuthenticationTokenBearerService);

  // The user object is modified in place when the outlet is switched, so every emission has to be treated as a change
  // to keep the information passed to the web component up to date.
  private readonly user: Signal<UserData> = toSignal(this.userService.user$, {equal: (): boolean => false});

  // The authentication information is rebuilt whenever the user or one of the tokens changes, so the profile never
  // works with outdated authentication information.
  private readonly authInfo: Signal<IAuthInfo> = toSignal(this.observeAuthInfo());

  protected readonly baseUrl: string = inject(CustomerDataFrontendService).getBaseUrl();

  protected readonly accountNumber: Signal<string> = this.customerDataProfileService.accountNumber;

  protected readonly profileVisible: Signal<boolean> = computed((): boolean => !!this.accountNumber());

  protected readonly locale: Signal<string> = computed((): string => this.user()?.getLocale());

  protected readonly businessPartnerId: Signal<string>
    = computed((): string => this.user()?.getBusinessPartner()?.getBusinessPartnerId());

  // The web component expects the authentication information as a JSON string.
  protected readonly authInfoJson: Signal<string>
    = computed((): string => this.authInfo() ? JSON.stringify(this.authInfo()) : undefined);

  protected handleCloseProfile(): void {
    this.customerDataProfileService.close();
  }

  protected handleSelectVehicle(event: Event): void {
    const vin: string = (event as CustomEvent)?.detail?.vin;
    if (!vin) {
      return;
    }
    this.customerDataProfileService.close();
    this.vehicleSearchService.closeAdvancedSearch();
    this.serviceCaseManager.selectAndOpenServiceCaseByVin(vin, VehicleSearchOrigin.CUSTOMER_DATA_FRONTEND);
    this.vehicleSearchService.postVehicleSearchAnalyticsEvents(VehicleSearchOrigin.CUSTOMER_DATA_FRONTEND, vin);
  }

  private observeAuthInfo(): Observable<IAuthInfo> {
    return combineLatest([
      this.userService.user$,
      this.authenticationTokenBearerService.oidcTokenSubject,
      this.authenticationTokenBearerService.csslTokenSubject
    ]).pipe(
      map(([user]: [UserData, unknown, unknown]): IAuthInfo => user
        ? AuthInfoFactory.makeAuthInfo(user, this.serviceCaseHolder.getActiveCase()?.getBrand())
        : undefined)
    );
  }
}
